import React from 'react';
import { Colors, Images, manageType } from 'utils';
import { IconAndTitle, PhoneRow, TextRow, StatusRow, StatusCard, SimpleTooltip } from 'components';
import payerPending from '../../assets/images/icons/payerPending.svg';
import payerRejected from '../../assets/images/icons/payerRejected.svg';
import payerCancelled from '../../assets/images/icons/payerCancelled.svg';
import payerAccepted from '../../assets/images/icons/payerAccepted.svg';

export const fsStatusEnums = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
};
export const payerTypeEnums = {
    PRIVATE: 'PRIVATE',
    PUBLIC: 'PUBLIC',
    SCHOOL: 'SCHOOL',
    REGIONAL_CENTER: 'REGIONAL_CENTER',
    WAIVER: 'WAIVER',
    PRIVATE_PAYER: 'PRIVATE_PAYER',
};
export const payerTypeOptions = [
    { name: 'Private Insurance', id: payerTypeEnums.PRIVATE },
    { name: 'Public Insurance', id: payerTypeEnums.PUBLIC },
    { name: 'Regional Center', id: payerTypeEnums.REGIONAL_CENTER },
    { name: 'Private Payer', id: payerTypeEnums.PRIVATE_PAYER },
    { name: 'School', id: payerTypeEnums.SCHOOL },
    { name: 'Waiver', id: payerTypeEnums.WAIVER },
];
/**
 * Payer statuses offered by the Status column filter.
 *
 * Exported rather than inlined in the head so the SelectedFilters chip bar can label a selection
 * from the same list the column offered it from. The column prepends its own `ALL` option; that is
 * a control, not a value — and note it is `ALL`, not the `All` every other column uses, which is
 * why `getFsSources` special-cases the string instead of relying on the shared handler's delete.
 */
export const fsStatusFilterList = [
    { name: 'Active', id: fsStatusEnums.ACTIVE },
    { name: 'Inactive', id: fsStatusEnums.INACTIVE },
];

export const fsHead = [
    { name: 'name', searchKey: 'NAME', title: 'Name' },
    {
        name: 'type',
        title: 'Type',
        disabled: true,
        width: '155px',
        filterList: [{ name: 'All', id: 'All' }, ...payerTypeOptions],
    },
    { name: 'address', searchKey: 'ADDRESS', rowName: 'formattedAddress', title: 'Address', custom: false },
    { name: 'email', searchKey: 'EMAIL', title: 'Email Address', width: '16%', custom: false },
    { name: 'phoneNumber', searchKey: 'PHONE', title: 'Phone Number', width: '150px', custom: false },
    {
        name: 'status',
        title: 'Status',
        filterTitle: 'Filter the Status',
        width: '120px',
        disabled: true,
        defaultSelected: fsStatusEnums.ACTIVE,
        filterList: [{ name: 'All', id: 'ALL' }, ...fsStatusFilterList],
    },
];

export const fsBody = [
    {
        rowText: (item) => (
            <div className="flex-align-center">
                <IconAndTitle icon={Images.fundingSourceOutline} title={item?.name} textWidth={10} />
                {renderPayerEnrollment(item)}
            </div>
        ),
    },
    { rowText: (item) => payerTypeOptions?.find((i) => i?.id === item?.type)?.name },
    { rowText: (item) => <TextRow name={item?.address?.formattedAddress ? item?.address?.formattedAddress : '--'} /> },
    { rowText: (item) => <TextRow name={item?.email} /> },
    { rowText: (item) => <PhoneRow phone={item?.phoneNumber ? item?.phoneNumber : ''} /> },
    { rowText: (item) => <StatusCard status={item?.status} /> },
];

export const ACTION_TYPE = 'GET_FUNDING_SOURCE';

export const FoundingStatuses = [
    { label: 'Active', value: 'ACTIVE', color: Colors.ThemeGreen },
    { label: 'Inactive', value: 'INACTIVE', color: Colors.TextSecondary },
];

export const fsNotYet = {
    title: 'No Payers Added Yet',
    subTitle: 'Start adding your funding sources to ensure accurate billing and financial tracking.',
    image: Images.noFsYet,
};

export const payerEnrollmentEnums = {
    PROVIDER_ACTION_REQUIRED: 'PROVIDER_ACTION_REQUIRED', // The enrollment requires action from the healthcare provider to proceed,
    PROVISIONING: 'PROVISIONING', //Stedi has begun the process of completing the enrollment with the payer.
    LIVE: 'LIVE', //The enrollment process is complete, and the specified provider can begin exchanging the listed transaction types with the payer.
    REJECTED: 'REJECTED', //The payer rejected the enrollment.
    CANCELED: 'CANCELED', //The enrollment has been terminated per customer or provider request.
};
export const renderPayerEnrollment = (payer) => {
    const status = payer?.enrollment?.status;
    const transaction = payer?.enrollment?.transition;
    const submissionChecked = transaction?.find((i) => i === 'professionalClaimSubmission');
    const eligibilityChecked = transaction?.find((i) => i === 'eligibilityCheck');
    const claimStatusChecked = transaction?.find((i) => i === 'claimStatus');
    const claimPaymentChecked = transaction?.find((i) => i === 'claimPayment');

    if (!status) {
        return '';
    }

    return (
        <div>
            {status === payerEnrollmentEnums?.PROVISIONING && (
                <SimpleTooltip
                    arrow
                    placement="top-start"
                    title={
                        <div>
                            <p>Processing Enrollment </p>
                            <p>
                                Capabilities: <span style={{ fontWeight: 600 }}>Submission, Eligibility</span>
                            </p>
                        </div>
                    }
                >
                    <img src={Images.payerPending} alt="pending" />
                </SimpleTooltip>
            )}

            {status === payerEnrollmentEnums?.REJECTED && (
                <SimpleTooltip
                    arrow
                    placement="top-start"
                    title={
                        <div>
                            <p>Rejected by Payer</p>
                            <p>
                                Reason: <span style={{ fontWeight: 600 }}>{payer?.enrollment?.reason}</span>
                            </p>
                        </div>
                    }
                >
                    <img src={Images.payerRejected} alt="rejected" />
                </SimpleTooltip>
            )}

            {status === payerEnrollmentEnums?.CANCELED && (
                <SimpleTooltip
                    arrow
                    placement="top-start"
                    title={
                        <div>
                            <p>Canceled by Provider</p>
                        </div>
                    }
                >
                    <img src={Images.payerCancelled} alt="cancelled" />
                </SimpleTooltip>
            )}

            {status === payerEnrollmentEnums?.PROVIDER_ACTION_REQUIRED && (
                <SimpleTooltip
                    arrow
                    placement="top-start"
                    title={
                        <div>
                            <p>Provider Action Required</p>
                            <p>Capabilities pending:</p>
                            <p> Provider action needed to continue enrollment</p>
                        </div>
                    }
                >
                    <img src={Images.payerRequired} alt="accepted" />
                </SimpleTooltip>
            )}

            {status === payerEnrollmentEnums?.LIVE && (
                <SimpleTooltip
                    arrow
                    placement="top-start"
                    title={
                        <div>
                            <p>Enrolled with TL Clearinghouse</p>
                            <p>Capabilities:</p>
                            <div>
                                <p>
                                    {submissionChecked ? '✅' : '❌'} Submission, {eligibilityChecked ? '✅' : '❌'} Eligibility
                                </p>
                                <p>
                                    {claimStatusChecked ? '✅' : '❌'} Claim Status, {claimPaymentChecked ? '✅' : '❌'} Claim Payment
                                </p>
                            </div>
                        </div>
                    }
                >
                    <img src={Images.payerAccepted} alt="accepted" />
                </SimpleTooltip>
            )}
        </div>
    );
};
